import useAxios, { configure, Options } from 'axios-hooks';
import { axiosInstance as apiClient, createApiClient, nonInterceptedAxiosInstance } from '@/index';
import UrlAssembler from 'url-assembler';
import getAryaConfig from '@/utils/getAryaConfig';
import {
  Action,
  ActionState,
  ActionType,
  ActivityLog,
  AddEmployeeLicensePayload,
  ApprovalRequest,
  AryaScore,
  AryaScoreHistory,
  AryaScoreVersion,
  AvailabilityOutput,
  BonusCapacity,
  BulkApplyPayload,
  BulkApplyResponse,
  BulkApproveShiftsResponse,
  BulkCreateEmployeePayload,
  BulkEditShiftsInput,
  BulkEditShiftsOutput,
  BulkRejectShiftsResponse,
  BulkShiftUploadReturn,
  BulkUserCreationResponse,
  CalendarEligibleEmployeesInput,
  CalendarShift,
  CalendarShifts,
  CalendarSyncConnector,
  CapacityRequest,
  CapacityRequestByIdResponse,
  CapacityRequestCapacityPayload,
  ComplianceFilterInput,
  CreateActionInput,
  CreateAttributePolicyPayload,
  CreateAttributePolicyResponse,
  CreateAvailabilityInput,
  CreateLocationPayload,
  CurrentEmployee,
  Customer,
  DashboardEndpoints,
  DashboardFilters,
  DemandDataUpload,
  DocumentApprovalAction,
  Employee,
  EmployeeAvailability,
  EmployeeAvailabilityOutput,
  EmployeeCalendarShift,
  EmployeeOptionsByEntityProps,
  EmployeePermissions,
  EmployeePreference,
  EmployeeProfilePayload,
  EmployeeProfileResponse,
  EmployeeRoleResourcePermissionResponse,
  EmployeeRoleResponse,
  EmployeesBulkArchivePayload,
  EmployeeScoreHistory,
  EmployeeT,
  EmployeeTUnique,
  EngagedEmployee,
  Entity,
  EntityEmploymentArchivePayload,
  EntityEmploymentArchiveResponse,
  EntityFilters,
  EntityMaxClinicianSettingsPayload,
  EntityMaxCliniciansSettingsResponse,
  EntitySettings,
  FetchAvailabilityInput,
  FetchBonusCapacityInput,
  FetchCalendarShiftsInput,
  FetchCSVInput,
  FetchEmployeesResponse,
  FetchTimekeepingCSVInput,
  FetchTimekeepingInput,
  ICustomersRouteResponse,
  IntakeQueueFilters,
  IntakeQueuePatient,
  JobRole,
  License,
  Location,
  ManagedEmployeeResponse,
  MultitenantUser,
  OpenShiftByPatientEntity,
  OpenShiftPayload,
  OpenShiftsFilterInput,
  PatientApiResult,
  PatientDetails,
  Performer,
  PreferenceCategoryType,
  PTOShift,
  QualityParam,
  ResourcePermission,
  RolesResponse,
  ScheduleGraphData,
  SchedulingFilterInput,
  ScoreParam,
  Service,
  ShiftDetailsByIdsOutput,
  ShiftRequest,
  ShiftRequestStatus,
  TimeKeeping,
  TimeKeepingGraphData,
  TimeOffHistoryResponse,
  TimeOffRequest,
  UnfilledShift,
  UpdateAttributePolicyPayload,
  UpdateEmployeeResourcePermissionsPayload,
  UpdateScoreInput,
  UserContext,
} from '@/service/Api/types';

export const API_URL = getAryaConfig('REST_API_URL');
const WAGES_API_URL = getAryaConfig('WAGES_API_URL');
const ARYA_AGENTS_API_URL = getAryaConfig('ARYA_AGENTS_API_URL');

const ApiService = {
  init(token: string) {
    const axiosInstance = createApiClient(API_URL, token);
    configure({ axios: axiosInstance });
  },

  verifyToken(token: string) {
    const url = new UrlAssembler().segment('/services/verify_token').toString();
    const opts = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    return apiClient.post<{ verified: boolean }>(url, {}, opts);
  },

  ssoAuthenticate(variables: { code: string }) {
    const url = new UrlAssembler().segment('/services/sso/auth').toString();
    return apiClient.post<{
      data: {
        refresh_token: string;
        token: string;
        isAdmin: boolean;
        user: {
          permissions: EmployeePermissions[];
        };
      };
    }>(url, variables);
  },

  authenticate(variables: { username: string; password: string; orgId?: string }) {
    const url = new UrlAssembler().segment('/services/auth').toString();
    return apiClient.post<{
      data: {
        isAdmin: boolean;
        refresh_token: string;
        token: string;
        user: {
          permissions: EmployeePermissions[];
        };
        featureFlags: {
          enableAdminDashboard: boolean;
          disableWorkerDashboard: boolean;
        };
      };
    }>(url, variables);
  },

  otpAuthVerify(variables: {
    userId: string | undefined;
    userEmail: string | undefined;
    totp: string;
    orgId?: string;
  }) {
    const url = new UrlAssembler().segment('/services/auth/totp/verify').toString();
    return nonInterceptedAxiosInstance.post(url, variables);
  },

  useURLExpander(hash: string, options?: Options) {
    const url = new UrlAssembler().segment('/services/auth/expand-url').param('hash', hash).toString();
    return useAxios(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  fetchMultitenantLookup(variables: { email: string }) {
    const url = new UrlAssembler().segment('/services/auth/multitenant-lookup').toString();
    return apiClient.post<{
      users: Array<MultitenantUser>;
      hasMultipleTenants: boolean;
    }>(url, variables);
  },

  forgotPassword(variables: { email: string }) {
    const url = new UrlAssembler()
      .segment('/services/users/forgot_password')
      .param('email', variables?.email)
      .toString();
    return apiClient.get(url);
  },

  resetPassword(variables: { code: string; password: string; email: string }) {
    const url = new UrlAssembler().segment('/services/users/reset_password').param('code', variables?.code).toString();
    return apiClient.post(url, { user: { email: variables?.email, password: variables?.password } });
  },

  useLoginWithCode(data: UserContext, options?: Options) {
    const url = new UrlAssembler().segment('/services/users/login_with_code').toString();
    return useAxios(
      {
        url: url,
        method: 'POST',
        data,
      },
      options
    );
  },

  useFetchWidgets(options?: Options) {
    const url = new UrlAssembler().segment('/services/widgets').toString();
    return useAxios(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  regenerateEmployeeCustomerMetadata(customerId: string) {
    const url = new UrlAssembler()
      .segment('/services/employees/customer/:customerId/regenerate_metadata')
      .param('customerId', customerId)
      .toString();
    return apiClient.post(url);
  },

  updateWidgetById(id: string, payload: any) {
    const url = new UrlAssembler().segment('/services/widgets/:id').param('id', id).toString();
    return apiClient.patch(url, payload);
  },
  downloadPaychexCSV(params: { customerId: string; start?: string | null; end?: string | null }) {
    const url = new UrlAssembler()
      .segment('/services/paychex')
      .query({ ...params })
      .toString();
    return apiClient.get(decodeURIComponent(url));
  },
  fetchCounties() {
    const url = new UrlAssembler().segment('/wds/counties').toString();
    return apiClient.get(url, {
      auth: {
        username: '812298c7-df24-406b-9ed8-e0ad8c75339c',
        password: 'fc6f5dd50fcc4f1784a225d1876815e0',
      },
    });
  },
  findTitles(
    county: string,
    jobTitle: string,
    jobDescription: string,
    projectStartDate: string,
    workDate: string,
    selectedConstructionType: string
  ) {
    // .segment('/wds/matching_wages')
    const url = new UrlAssembler(WAGES_API_URL)
      .segment('/titles')
      .query({ county, jobTitle, jobDescription, projectStartDate, workDate, selectedConstructionType })
      .toString();
    return apiClient.get(url, {
      auth: {
        username: '812298c7-df24-406b-9ed8-e0ad8c75339c',
        password: 'fc6f5dd50fcc4f1784a225d1876815e0',
      },
    });
  },
  findWages(
    county: string,
    jobTitle: string,
    jobDescription: string,
    projectStartDate: string,
    workDate: string,
    selectedConstructionType: string,
    selectedTitleData: any
  ) {
    // .segment('/wds/matching_wages')
    const url = new UrlAssembler(WAGES_API_URL)
      .segment('/wages')
      .query({ county, jobTitle, jobDescription, projectStartDate, workDate, selectedConstructionType })
      .toString();
    return apiClient.post(url, {
      auth: {
        username: '812298c7-df24-406b-9ed8-e0ad8c75339c',
        password: 'fc6f5dd50fcc4f1784a225d1876815e0',
      },
      ...selectedTitleData,
    });
  },

  // id: employeeId or userId
  useFetchEmployeeProfile(
    variables: {
      id: string;
      includeArchivedLicenses?: boolean;
    },
    options?: Options
  ) {
    const url = new UrlAssembler()
      .segment(`/services/employees/:id/profile`)
      .param('id', variables.id)
      .query({ includeArchivedLicenses: variables.includeArchivedLicenses })
      .toString();
    return useAxios<EmployeeProfileResponse>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  // id: employeeId or userId
  useFetchAdminEmployeeProfile(
    variables: {
      id: string;
      includeArchivedLicenses?: boolean;
    },
    options?: Options
  ) {
    const url = new UrlAssembler()
      .segment(`/services/admin/employee/:id/profile`)
      .param('id', variables.id)
      .query({ includeArchivedLicenses: variables.includeArchivedLicenses })
      .toString();
    return useAxios<EmployeeProfileResponse>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  updateEmployeeProfile(employeeId: string, payload: EmployeeProfilePayload) {
    const url = new UrlAssembler()
      .segment(`/services/employees/:employeeId/profile`)
      .param('employeeId', employeeId)
      .toString();
    return apiClient.post(url, { profile: payload });
  },

  useFetchEmployeeAvailability(
    params: {
      id?: string;
      startTime?: string;
      endTime?: string;
    },
    options?: Options
  ) {
    const url = new UrlAssembler()
      .segment(`/services/availability/employee/:id`)
      .param({ ...params })
      .toString();

    return useAxios<EmployeeAvailability[]>(
      {
        url: url,
        method: 'POST',
      },
      options
    );
  },

  useFetchEmployeeSchedule(
    params: {
      employeeId: string;
      startTime: string;
      endTime: string;
      openShifts?: boolean;
      myTeamSchedule?: boolean;
    },
    options?: Options
  ) {
    const url = new UrlAssembler()
      .segment(`/services/employees/:employeeId/schedule`)
      .param({ ...params })
      .toString();
    return useAxios<{
      schedule: EmployeeCalendarShift[];
    }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  useFetchEmployeeOpenShifts(
    variables: {
      query: {
        numberOfShifts?: number | null;
        offset?: number | null;
        startTime: string;
        endTime?: string | null;
      };
    },
    options?: Options
  ) {
    const url = new UrlAssembler()
      .segment(`/services/employees/open_shifts_by_count`)
      .query({ ...variables.query })
      .toString();

    return useAxios<{
      numberOfOpenShifts: {
        shifts: EmployeeCalendarShift[];
        hasMoreShifts: boolean;
      };
    }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  // TODO: remove duplicate useFetchEmployeeOpenShifts
  fetchEmployeeOpenShifts(variables: {
    query: {
      numberOfShifts?: number;
      offset?: number;
      startTime?: string;
      endTime?: string;
    };
  }) {
    const url = new UrlAssembler()
      .segment(`/services/employees/open_shifts_by_count`)
      .query({ ...variables?.query })
      .toString();

    return apiClient.get<{
      numberOfOpenShifts: {
        shifts: EmployeeCalendarShift[];
        hasMoreShifts: boolean;
      };
    }>(url);
  },

  useFetchEmployeeScheduleById(
    params: {
      employeeId: string;
      shiftId: string;
      type: string | undefined | null;
    },
    options?: Options
  ) {
    const url = new UrlAssembler()
      .segment(`/services/employees/:employeeId/schedule/:shiftId`)
      .param({ ...params })
      .query({ type: params.type })
      .toString();
    return useAxios<{
      schedule: Omit<EmployeeCalendarShift, 'shiftRequests'> & { shiftRequests: ShiftRequest[] };
    }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  fetchEmployeeScheduleById(params: { employeeId: string; shiftId: string }) {
    const url = new UrlAssembler()
      .segment(`/services/employees/:employeeId/schedule/:shiftId`)
      .param({ ...params })
      .toString();
    return apiClient.get<{
      schedule: Omit<EmployeeCalendarShift, 'shiftRequests'> & { shiftRequests: ShiftRequest[] };
    }>(url);
  },

  useFetchTags(search: string, options?: Options) {
    const url = new UrlAssembler().segment('/services/calendar/tags').param('param', search).toString();

    return useAxios(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  useFetchCurrentEmployee(options?: Options) {
    const url = new UrlAssembler().segment(`/services/employees/me`).toString();
    return useAxios<{
      employee: CurrentEmployee;
    }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  useFetchEmployees<T extends EmployeeT | EmployeeTUnique>(query: EmployeeOptionsByEntityProps, options?: Options) {
    const url = new UrlAssembler()
      .segment('/services/employees')
      .query({ ...query })
      .toString();

    return useAxios<FetchEmployeesResponse<T>>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  createEmployee(payload: BulkCreateEmployeePayload) {
    const url = new UrlAssembler().segment('/services/employees/bulk').toString();

    const employees = {
      jobRoleId: payload.jobRoleId,
      locationId: payload.locationId,
      employeeStatus: 'approved',
      users: payload.employees,
      keepEmployments: true,
    };

    return apiClient.post(url, { employees });
  },

  useFetchEntitiesWithFilters(filters: EntityFilters, permissionAttributes?: string[], options?: Options) {
    const url = new UrlAssembler().segment('/services/entities/search').toString();
    return useAxios<{ entities: Entity[] }>(
      {
        url: url,
        method: 'POST',
        data: { filters, permissionAttributes },
      },
      options
    );
  },

  useFetchShiftTotals(type: DashboardEndpoints, params: DashboardFilters, options?: Options) {
    const url = new UrlAssembler().segment(`/services/dashboard/${type}`).toString();

    return useAxios(
      {
        url: url,
        method: 'POST',
        data: params,
      },
      options
    );
  },

  createShift(managerId: string, payload: any) {
    const url = new UrlAssembler()
      .segment('/services/calendar/:managerId/employee')
      .param('managerId', managerId)
      .toString();
    return apiClient.post(url, payload);
  },

  persistBulkShift(documentId: string, timezone: string, managerId: string) {
    const url = new UrlAssembler()
      .segment('/services/documents/bulk-shifts-upload/:documentId')
      .param('documentId', documentId)
      .toString();

    return apiClient.post(url, {
      timezone,
      managerId,
    });
  },

  useFetchEmployeeData(ids: string[], options?: Options) {
    const url = new UrlAssembler().segment('/services/employees/colors/').toString();
    return useAxios<{ employees: Employee[] }>(
      {
        url: url,
        data: { ids },
        method: 'POST',
      },
      options
    );
  },

  useFetchCalendarShifts(managerId: string, filters: FetchCalendarShiftsInput, options?: Options) {
    const url = new UrlAssembler().segment('/services/calendar/:managerId/').param('managerId', managerId).toString();

    return useAxios<{ schedule: CalendarShifts; totalRequestCount: number; totalCount: number }>(
      {
        url: url,
        data: { filters, eventsUi: true },
        method: 'POST',
      },
      options
    );
  },

  useFetchCalendarShift(id: string, options?: Options) {
    const url = new UrlAssembler().segment('/services/calendar/:id').param('id', id).toString();
    return useAxios<{ calendar: CalendarShift }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  fetchShiftsCSV(managerId: string, filters: FetchCSVInput, csvAsync?: boolean) {
    const url = new UrlAssembler().segment(`/services/calendar/:managerId`).param('managerId', managerId).toString();

    if (!csvAsync) {
      return nonInterceptedAxiosInstance.post(url, { filters: { ...filters } });
    }

    return apiClient.post(url, { filters: { ...filters }, csvAsync });
  },

  triggerCapacityRequestsReport() {
    const url = new UrlAssembler().segment('/services/reports/capacity_requests').toString();
    return apiClient.post(url);
  },

  fetchTimekeepingCSV(filters: FetchTimekeepingCSVInput) {
    const url = new UrlAssembler().segment(`/services/actions/timekeeping/csv`).toString();
    return apiClient.post(url, { filters: { ...filters } });
  },

  removeEmployeeFromCalendar(employeeId: string, calendarId: string, reason: string) {
    const url = new UrlAssembler()
      .segment(`/services/employees/:employeeId/schedule/:calendarId/remove`)
      .param('employeeId', employeeId)
      .param('calendarId', calendarId)
      .toString();

    return apiClient.put(url, {
      removeRequest: {
        reason,
      },
    });
  },

  assignEmployeeToCalendar(employeeId: string, calendarId: string) {
    const url = new UrlAssembler()
      .segment(`/services/employees/:employeeId/schedule/:calendarId/assign`)
      .param('employeeId', employeeId)
      .param('calendarId', calendarId)
      .toString();

    return apiClient.put(url, {
      calendar: {
        employeeId: employeeId,
      },
    });
  },

  applyEmployeeToCalendar(
    employeeId: string,
    calendarId: string,
    managerId: string,
    shiftType: string | undefined | null
  ) {
    const url = new UrlAssembler()
      .segment(`/services/employees/:id/schedule/:calendarId/apply`)
      .param('id', employeeId)
      .param('calendarId', calendarId)
      .toString();

    return apiClient.put(url, {
      applyRequest: {
        toEmployeeId: managerId,
      },
      type: shiftType,
    });
  },

  withdrawApplyRequest({ employeeId, calendarId }: { employeeId: string; calendarId: string }) {
    const url = new UrlAssembler()
      .segment(`/services/employees/:id/schedule/:calendarId/withdraw`)
      .param('id', employeeId)
      .param('calendarId', calendarId)
      .toString();

    return apiClient.put(url);
  },

  updateShiftEvent(id: string, payload: any) {
    const url = new UrlAssembler().segment('/services/calendar/:id').param('id', id).toString();

    return apiClient.put(url, payload);
  },

  useBulkUpdateShiftEvents(payload: BulkEditShiftsInput, options?: Options) {
    const url = new UrlAssembler().segment('/services/calendar/bulk-edit').toString();
    return useAxios<BulkEditShiftsOutput>(
      {
        url: url,
        data: payload,
        method: 'PATCH',
      },
      options
    );
  },

  useFetchShiftsByIds(shiftIds: string[], options?: Options) {
    const url = new UrlAssembler().segment('/services/calendar/bulk-details').toString();
    return useAxios<ShiftDetailsByIdsOutput>(
      {
        url: url,
        data: { shiftIds },
        method: 'POST',
      },
      options
    );
  },

  deleteShiftEvent(id: string, query: { isStandAlone?: boolean }) {
    const url = new UrlAssembler()
      .segment('/services/calendar/:id')
      .param('id', id)
      .query({ ...query })
      .toString();

    return apiClient.delete(url);
  },

  swapShiftForEmployee(employeeId: string, calendarId: string, swapToEmployeeId: string, reason: string) {
    const url = new UrlAssembler()
      .segment(`/services/employees/:employeeId/schedule/:calendarId/swap`)
      .param('employeeId', employeeId)
      .param('calendarId', calendarId)
      .toString();
    return apiClient.put(url, {
      swapRequest: {
        swapToEmployeeId,
        reason,
      },
    });
  },

  assignShiftForEmployee(employeeId: string, calendarId: string, assignToEmployeeId: string) {
    const url = new UrlAssembler()
      .segment(`/services/employees/:employeeId/schedule/:calendarId/assign`)
      .param('employeeId', employeeId)
      .param('calendarId', calendarId)
      .toString();
    return apiClient.put(url, {
      calendar: {
        employeeId: assignToEmployeeId,
      },
    });
  },

  getStatesForTimesheet() {
    const url = new UrlAssembler().segment('/services/action_states').toString();
    const params = { type: 'timesheet' };
    return apiClient.get(url, { params });
  },

  useFetchStatesForTimesheet(variables: { type: 'timesheet' }, options?: Options) {
    const url = new UrlAssembler()
      .segment(`/services/action_states`)
      .param({ ...variables })
      .toString();

    return useAxios<{
      actionStates: ActionState[];
    }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  getActionTypes() {
    const url = new UrlAssembler().segment('/services/action_types').toString();
    return apiClient.get(url);
  },

  useFetchActionTypes(options?: Options) {
    const url = new UrlAssembler().segment('/services/action_types').toString();
    return useAxios<{ actionTypes: ActionType[] }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  getActions(userId: string) {
    const url = new UrlAssembler().segment(`/services/actions/:userId`).param('userId', userId).toString();
    const params = { type: 'timesheet' };
    return apiClient.get(url, { params });
  },

  createPTO(
    employeeId: string,
    ptoDetails: {
      startTime: string;
      endTime: string;
      type: string;
      timezone: string;
      allDay: boolean;
      title: string;
      reason: string;
      entityId: string;
    }
  ) {
    const url = new UrlAssembler()
      .segment(`/services/employees/:employeeId/pto`)
      .param('employeeId', employeeId)
      .toString();

    return apiClient.post(url, { calendar: { ...ptoDetails, employeeId } });
  },

  bulkCreateActions(actionTypeId: string, actions: CreateActionInput[]) {
    const url = new UrlAssembler().segment('/services/actions/multi').toString();
    const data = {
      actionTypeId,
      actions,
    };
    return apiClient.post(url, data);
  },

  createAction(variables: CreateActionInput) {
    const url = new UrlAssembler().segment('/services/actions').toString();
    const data = {
      action: variables,
    };
    return apiClient.post(url, data);
  },

  bulkUpdateActions(actionTypeId: string, actions: Partial<CreateActionInput>[]) {
    const url = new UrlAssembler().segment('/services/actions/multi').toString();
    const data = {
      actionTypeId,
      actions,
    };
    return apiClient.put(url, data);
  },

  deleteActions(employeeId: string, shiftSequence: number) {
    const url = new UrlAssembler().segment('/services/actions/multi').toString();
    const data = {
      employeeId,
      shiftSequence,
    };
    return apiClient.delete(url, {
      data,
    });
  },

  deleteAction(id: string) {
    const url = new UrlAssembler().segment('/services/actions/:id').param('id', id).toString();

    return apiClient.delete(url);
  },

  useFetchJobRolesByEntityId(variables: { entityId: string }, options?: Options) {
    const url = new UrlAssembler()
      .segment(`/services/entities/:entityId/job_roles`)
      .param({ ...variables })
      .toString();

    return useAxios<{ jobRoles: JobRole[] }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  createJobRole(entityId: string, jobRoleData: { title: string; category: string }) {
    const url = new UrlAssembler()
      .segment(`/services/entities/:entityId/job_roles`)
      .param('entityId', entityId)
      .toString();

    return apiClient.post(url, { jobRoles: [jobRoleData] });
  },

  useFetchServices(options?: Options) {
    const url = new UrlAssembler().segment(`/services/calendar/services/all`).toString();

    return useAxios<{ services: Service[] }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  useFetchLocationsByEntity(variables: { entityId: string }, options?: Options) {
    const url = new UrlAssembler()
      .segment(`/services/entities/:entityId/location`)
      .param({ ...variables })
      .toString();

    return useAxios<{ locations: Array<Location> }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  createLocationByEntity(variables: { entityId: string }, location: CreateLocationPayload) {
    const url = new UrlAssembler()
      .segment(`/services/entities/:entityId/location`)
      .param({ ...variables })
      .toString();

    return apiClient.post(url, { location });
  },

  checkIfInside(locationId: string, latitude: number, longitude: number) {
    const url = new UrlAssembler()
      .segment(`/services/entities/locations/:locationId/contains`)
      .param('locationId', locationId)
      .toString();
    const params = { lat: latitude, lng: longitude };
    return apiClient.get(url, { params });
  },

  useFetchEntities(options?: Options) {
    const url = new UrlAssembler().segment('/services/entities').toString();
    return useAxios<{
      entities: Entity[];
    }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  createEntity(entityData: { name: string; description: string }) {
    const url = new UrlAssembler().segment('/services/entities').toString();
    const entity = {
      name: entityData.name,
      description: entityData.description,
      is_active: true,
      img_url: 'http://via.placeholder.com/500x500',
      opt_in_required: false,
      notifications: {},
      eligibility: {},
      approval: {},
      metadata: {},
      slug: entityData.name.toLowerCase().replace(/ /g, '-'),
    };
    return apiClient.post(url, { entity });
  },

  updateShiftStatus(
    requestId: string,
    calendarId: string,
    payload: {
      requestType: string;
      requestStatus: ShiftRequestStatus.Approved | ShiftRequestStatus.Rejected | ShiftRequestStatus.InReview;
      reason?: string;
    }
  ) {
    const url = new UrlAssembler()
      .segment('/services/approvals/:requestId/calendar/:calendarId')
      .param('requestId', requestId)
      .param('calendarId', calendarId)
      .toString();

    return apiClient.put(url, { shiftRequests: payload });
  },

  useFetchTimekeepingActions(filters: FetchTimekeepingInput, options?: Options) {
    const url = new UrlAssembler().segment('/services/actions/timekeeping').toString();

    return useAxios<{ timekeeping: TimeKeeping[] }>(
      {
        url: url,
        data: { filters },
        method: 'POST',
      },
      options
    );
  },

  useFetchTimekeepingGraphData(filters: FetchTimekeepingInput, options?: Options) {
    const url = new UrlAssembler().segment('/services/actions/timekeeping/graph').toString();
    return useAxios<TimeKeepingGraphData[]>(
      {
        url: url,
        data: { filters },
        method: 'POST',
      },
      options
    );
  },

  useFetchScheduleGraphData(filters: FetchCalendarShiftsInput, options?: Options) {
    const url = new UrlAssembler().segment('/services/calendar/schedule/graph').toString();
    return useAxios<ScheduleGraphData[]>(
      {
        url: url,
        data: { filters },
        method: 'POST',
      },
      options
    );
  },

  getPendingPTORequests(): Promise<{
    data: {
      shiftRequests: PTOShift[];
    };
  }> {
    const url = new UrlAssembler().segment(`/services/approvals`).toString();
    const params = {
      statuses: 'pending',
      request: 'shifts',
      types: 'pto',
      withHistory: false,
    };
    return apiClient.get(url, { params });
  },

  getCompletedPTORequests(): Promise<{
    data: {
      shiftRequests: PTOShift[];
    };
  }> {
    const url = new UrlAssembler().segment(`/services/approvals`).toString();
    const params = {
      statuses: 'approved,rejected',
      request: 'shifts',
      types: 'pto',
      withHistory: false,
    };
    return apiClient.get(url, { params });
  },

  getPendingActionRequests(): Promise<{
    data: {
      actionRequests: ApprovalRequest[];
    };
  }> {
    const url = new UrlAssembler().segment(`/services/approvals`).toString();
    const params = {
      statuses: 'pending',
      request: 'actions',
      types: 'verification',
      withHistory: false,
    };
    return apiClient.get(url, { params });
  },

  getCompletedActionRequests(): Promise<{
    data: {
      actionRequests: ApprovalRequest[];
    };
  }> {
    const url = new UrlAssembler().segment(`/services/approvals`).toString();
    const params = {
      statuses: 'approved,rejected',
      request: 'actions',
      types: 'verification',
      withHistory: false,
    };
    return apiClient.get(url, { params });
  },

  approveActionRequest(requestId: string) {
    const url = new UrlAssembler().segment(`/services/approvals/actions`).toString();
    return apiClient.put(url, {
      requests: [
        {
          id: requestId,
          requestStatus: 'approved',
          reason: 'Approved',
        },
      ],
    });
  },
  approveShiftRequest(requestId: string, employeeId: string, calendarId: string) {
    const url = new UrlAssembler().segment(`/services/approvals/shifts`).toString();
    return apiClient.put(url, {
      shiftRequests: [
        {
          id: requestId,
          employeeId,
          calendarId,
          requestType: 'pto',
          requestStatus: 'approved',
          reason: 'Approved',
        },
      ],
    });
  },
  rejectActionRequest(requestId: string, reason: string) {
    const url = new UrlAssembler().segment(`/services/approvals/actions`).toString();
    return apiClient.put(url, {
      requests: [
        {
          id: requestId,
          requestStatus: 'rejected',
          reason,
        },
      ],
    });
  },
  rejectShiftRequest(requestId: string, employeeId: string, calendarId: string, reason: string) {
    const url = new UrlAssembler().segment(`/services/approvals/shifts`).toString();
    return apiClient.put(url, {
      shiftRequests: [
        {
          id: requestId,
          employeeId,
          calendarId,
          requestType: 'pto',
          requestStatus: 'rejected',
          reason,
        },
      ],
    });
  },
  bulkApproveActionRequests(requests: string[]) {
    const url = new UrlAssembler().segment(`/services/approvals/actions`).toString();
    return apiClient.put(url, {
      requests: requests.map((id) => ({
        id,
        requestStatus: 'approved',
        reason: 'Approved',
      })),
    });
  },

  bulkApproveShiftRequests(
    approveRequests: {
      requestId: string;
      employeeId: string;
      calendarId: string;
    }[]
  ) {
    const url = new UrlAssembler().segment(`/services/approvals/shifts`).toString();
    return apiClient.put(url, {
      shiftRequests: approveRequests.map((approveRequest) => ({
        id: approveRequest.requestId,
        employeeId: approveRequest.employeeId,
        calendarId: approveRequest.calendarId,
        requestType: 'pto',
        requestStatus: 'approved',
        reason: 'Approved',
      })),
    });
  },

  bulkRejectActionRequests(requests: string[]) {
    const url = new UrlAssembler().segment(`/services/approvals/actions`).toString();
    return apiClient.put(url, {
      requests: requests.map((id) => ({
        id,
        requestStatus: 'rejected',
        reason: 'Rejected',
      })),
    });
  },

  bulkRejectShiftRequests(
    rejectRequests: {
      requestId: string;
      employeeId: string;
      calendarId: string;
    }[]
  ) {
    const url = new UrlAssembler().segment(`/services/approvals/shifts`).toString();
    return apiClient.put(url, {
      shiftRequests: rejectRequests.map((rejectRequest) => ({
        id: rejectRequest.requestId,
        employeeId: rejectRequest.employeeId,
        calendarId: rejectRequest.calendarId,
        requestType: 'pto',
        requestStatus: 'rejected',
        reason: 'Rejected',
      })),
    });
  },

  editActionsAndApprove(actionTypeId: string, approvalRequestId: string, actions: Action[]) {
    const url = new UrlAssembler()
      .segment(`/services/approvals/:requestId/actions`)
      .param('requestId', approvalRequestId)
      .toString();
    return apiClient.put(url, { actionTypeId, actions, reason: '' });
  },

  useBulkShiftsUpload(options?: Options) {
    const url = new UrlAssembler().segment('/services/documents/shifts_upload').toString();
    return useAxios<BulkShiftUploadReturn>(
      {
        url: url,
        method: 'POST',
        headers: {
          'Content-Type': 'multipart/form-data; boundary=<calculated when request is sent>',
        },
      },
      options
    );
  },

  useFetchPerformanceScores(
    queryParams?: {
      employeeName?: string;
      sortBy?: string;
      sortOrder?: string;
      startDate?: string;
      endDate?: string;
      aryaScoreVersionId: string;
      qualityParamVersionId?: string;
    },
    options?: Options
  ) {
    const url = new UrlAssembler()
      .segment('/services/scores')
      .query({ ...queryParams })
      .toString();

    return useAxios<{
      aryaScores: AryaScore[];
    }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  useFetchCurrentScoresVersion(options?: Options) {
    const url = new UrlAssembler().segment('/services/scores/versions/current').toString();

    return useAxios<{
      aryaScoreVersions: AryaScoreVersion[];
    }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  updateCurrentScoresVersion(payload: UpdateScoreInput) {
    const url = new UrlAssembler().segment('/services/scores/versions').toString();
    const data = {
      aryaScoreVersion: payload,
    };
    return apiClient.post(url, data);
  },

  useFetScoreParameters(
    employeeId: string,
    queryParams?: Partial<{
      qualityParamIds: string;
      startDate: string;
      endDate: string;
    }>,
    options?: Options
  ) {
    const url = new UrlAssembler()
      .segment('/services/scores/score_params/:employeeId')
      .param('employeeId', employeeId)
      .query({ ...queryParams })
      .toString();
    return useAxios<{
      scoreParams: ScoreParam[];
    }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  useFetchHistoryScores(
    queryParams?: {
      aryaScoreVersionId: string;
    } & Partial<{
      employeeId: string;
      startDate: string;
      endDate: string;
      qualityParamVersionId: string;
    }>,
    options?: Options
  ) {
    const url = new UrlAssembler()
      .segment('/services/scores/history')
      .query({ ...queryParams })
      .toString();
    return useAxios<{
      aryaScoreHistory: Record<number, AryaScoreHistory>;
    }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  // TODO: Handle employees in multiple user groups
  useGetSelfOverAllScores() {
    const url = new UrlAssembler().segment('/services/employees/me/score').toString();
    return useAxios<{
      aryaScores: Array<AryaScore>;
    }>({
      url: url,
      method: 'GET',
    });
  },

  // TODO: Handle employees in multiple user groups
  useGetSelfScoreHistory(queryParams?: { qualityParamId?: string; startDate?: string; endDate?: string }) {
    const url = new UrlAssembler()
      .segment('/services/employees/me/score/history')
      .query({ ...queryParams })
      .toString();

    return useAxios<{
      aryaScoreHistories: Array<EmployeeScoreHistory[]>;
    }>({
      url: url,
      method: 'GET',
    });
  },

  useFetchQualityParameters(options?: Options) {
    const url = new UrlAssembler().segment('/services/scores/quality_params').toString();
    return useAxios<{ qualityParams: QualityParam[] }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  useQualityParamsUpload(options?: Options) {
    const url = new UrlAssembler().segment('/services/scores/quality_data').toString();
    return useAxios<Array<any>>(
      {
        url: url,
        method: 'POST',
        headers: {
          'Content-Type': 'multipart/form-data; boundary=<calculated when request is sent>',
        },
        params: {
          ignoreBadTypes: 'true',
        },
      },
      options
    );
  },

  useDemandShiftsUpload(documentType: string, entityName?: string, timezone?: string, options?: Options) {
    const url = new UrlAssembler().segment('/services/documents/upload_demand').toString();
    return useAxios<DemandDataUpload>(
      {
        url: url,
        method: 'POST',
        headers: {
          'Content-Type': 'multipart/form-data; boundary=<calculated when request is sent>',
        },
        params: {
          entityName,
          timezone,
          documentType,
        },
      },
      options
    );
  },

  publishDemandData(variables: {
    demandId: string;
    sendNotifications?: boolean;
    notificationTarget?: 'all' | 'tiering';
  }) {
    const url = new UrlAssembler()
      .segment('/services/calendar/demand/:demandId')
      .param('demandId', variables.demandId)
      .toString();
    return apiClient.post<{ success: string }>(url, {
      sendNotifications: variables.sendNotifications,
      notificationTarget: variables.notificationTarget,
    });
  },

  useFetchCalendarStates(options?: Options) {
    const url = new UrlAssembler().segment('/services/calendar/states/all').toString();
    return useAxios<{
      states: {
        id: string;
        name: string;
        abbreviation?: string | null;
      }[];
    }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  useFetchCalendarEligibleEmployees<T extends EmployeeT | EmployeeTUnique>(
    filters?: CalendarEligibleEmployeesInput,
    options?: Options
  ) {
    const url = new UrlAssembler().segment('/services/calendar/eligible_employees').toString();
    return useAxios<FetchEmployeesResponse<T>>(
      {
        url: url,
        data: { filters },
        method: 'POST',
      },
      options
    );
  },

  useSendAvailabilityChatAgentMessage(hookOptions?: {
    data?: { employee_id?: string; message?: string; session_id?: string; intent: string; props?: Record<string, any> };
    access_token?: string;
    options?: Options;
  }) {
    const url = new UrlAssembler(ARYA_AGENTS_API_URL).prefix('/api/v2').segment('/chat/').toString();
    return useAxios(
      {
        url: url,
        method: 'POST',
        data: {
          ...hookOptions?.data,
          intent: 'availability',
        },
        headers: {
          Authorization: `Bearer ${hookOptions?.access_token}`,
          'Content-Type': 'multipart/form-data; boundary=<calculated when request is sent>',
        },
      },
      hookOptions?.options
    );
  },

  useSendPhotoUploadChatAgentMessage(hookOptions?: {
    data?: { employee_id?: string; message?: string; session_id?: string; intent: string; props?: Record<string, any> };
    access_token?: string;
    options?: Options;
  }) {
    const url = new UrlAssembler(ARYA_AGENTS_API_URL).prefix('/api/v2').segment('/chat/').toString();
    return useAxios(
      {
        url: url,
        method: 'POST',
        data: {
          ...hookOptions?.data,
          intent: 'profile_photo',
        },
        headers: {
          Authorization: `Bearer ${hookOptions?.access_token}`,
          'Content-Type': 'multipart/form-data; boundary=<calculated when request is sent>',
        },
      },
      hookOptions?.options
    );
  },

  useSendComplianceChatAgentMessage(hookOptions?: {
    data?: {
      employee_id?: string;
      message?: string;
      intent: string;
      session_id?: string;
      documentType?: string;
      file?: FormData;
      props?: Record<string, any>;
    };
    access_token?: string;
    options?: Options;
  }) {
    const url = new UrlAssembler(ARYA_AGENTS_API_URL).prefix('/api/v1').segment('/chat/').toString();
    return useAxios(
      {
        url: url,
        method: 'POST',
        data: {
          ...hookOptions?.data,
          intent: 'compliance',
        },
        headers: {
          Authorization: `Bearer ${hookOptions?.access_token}`,
          'Content-Type': 'multipart/form-data; boundary=<calculated when request is sent>',
        },
      },
      hookOptions?.options
    );
  },

  useCreateChatAgentMessageToken(hookOptions?: { data?: { orgId: string }; options?: Options }) {
    const url = new UrlAssembler(ARYA_AGENTS_API_URL).segment('/token').toString();
    return useAxios(
      {
        url: url,
        method: 'POST',
        data: {
          claims: {
            org_id: hookOptions?.data?.orgId,
            service_name: 'agentic',
            permissions: ['read', 'write'],
          },
        },
      },
      hookOptions?.options
    );
  },

  createEmployeeAvailability(variables: CreateAvailabilityInput) {
    const url = new UrlAssembler().segment('/services/availability/').toString();
    return apiClient.post(url, variables);
  },

  removeEmployeeAvailability(variables: { id: string }) {
    const url = new UrlAssembler()
      .segment('/services/availability/:id')
      .param({ ...variables })
      .toString();
    return apiClient.delete(url);
  },

  updateEmployeeAvailability(variables: { id: string }, body: Partial<EmployeeAvailability>) {
    const url = new UrlAssembler()
      .segment('/services/availability/:id')
      .param({ ...variables })
      .toString();
    return apiClient.put(url, body);
  },

  useFetchGoogleCalendarOAuth(hookOptions: { variables?: { connectorId?: string }; options?: Options }) {
    const url = new UrlAssembler().segment('/services/calendar/subscribe/google-oauth').toString();
    return useAxios<{ authUrl: string }>(
      {
        url: url,
        data: { ...hookOptions.variables },
        method: 'POST',
      },
      hookOptions.options
    );
  },

  useCreateGoogleCalendar(variables?: { connectorId?: string }, options?: Options) {
    const url = new UrlAssembler().segment('/services/calendar/subscribe/create-google-calendar').toString();
    return useAxios(
      {
        url: url,
        data: { ...variables },
        method: 'POST',
      },
      options
    );
  },

  useFetchEmployeeCalendarConnectors(options?: Options) {
    const url = new UrlAssembler().segment('/services/calendar/subscribe/calendar-connectors').toString();
    return useAxios<{ connectors: Array<CalendarSyncConnector> }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  useFetchRollingSchedule(employeeId: string, options?: Options) {
    const url = new UrlAssembler()
      .segment('/services/employees/:employeeId/rolling_schedule')
      .param({ employeeId })
      .toString();
    return useAxios(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  useCreateCalendarSubscriptionUrl(
    variables?: {
      type?: string;
      filters?: Partial<FetchCalendarShiftsInput>;
    },
    options?: Options
  ) {
    const url = new UrlAssembler().segment('/services/calendar/subscribe/create-url').toString();
    return useAxios<{ connector: Omit<CalendarSyncConnector, 'calendarId'> }>(
      {
        url: url,
        data: { type: variables?.type, filters: variables?.filters },
        method: 'POST',
      },
      options
    );
  },

  archiveCalendarSyncConnector(id: string) {
    const url = new UrlAssembler()
      .segment('/services/calendar/subscribe/calendar-connectors/:id')
      .param('id', id)
      .toString();
    return apiClient.delete<{ message: string }>(url);
  },

  useFetchNextShift(
    params: {
      employeeId: string;
    },
    options?: Options
  ) {
    const url = new UrlAssembler()
      .segment(`/services/employees/:employeeId/next-shift`)
      .param({ ...params })
      .toString();
    return useAxios(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  useFetchAvailabilityByEmployee(variables?: FetchAvailabilityInput, options?: Options) {
    const url = new UrlAssembler().segment('/services/availability/all').toString();
    return useAxios<{
      totalCount: number;
      availabilities: EmployeeAvailabilityOutput[];
    }>(
      {
        url: url,
        data: { ...variables },
        method: 'POST',
      },
      options
    );
  },

  useFetchAvailability(variables?: FetchAvailabilityInput, options?: Options) {
    const url = new UrlAssembler().segment('/services/availability/get_availabilities').toString();
    return useAxios<{
      totalCount: number;
      availabilities: AvailabilityOutput[];
    }>(
      {
        url: url,
        data: { ...variables },
        method: 'POST',
      },
      options
    );
  },

  useCreateScheduledCall(
    variables?: {
      shiftIds: string[];
      scheduledFor?: string;
    },
    options?: Options
  ) {
    const url = new UrlAssembler().segment('/services/scheduled_communications/schedule_call').toString();
    return useAxios<{ scheduledCommunication: Record<any, any> }>(
      {
        url: url,
        data: variables,
        method: 'POST',
      },
      options
    );
  },

  fetchUserAvailability(
    employeeId: string,
    variables?: Partial<{
      startTime: string | null;
      endTime: string | null;
    }>
  ) {
    const url = new UrlAssembler().segment('/services/availability/employee/:id').param('id', employeeId).toString();
    return apiClient.post<
      {
        availability_type: 'available' | 'unavailable';
        available_from: string;
        available_minutes?: string | null;
        available_to: string;
        employee_id: string;
        id: string;
        metadata?: Record<string, any>;
      }[]
    >(url, variables);
  },

  useFetchCustomers(hookOptions: { query: { query?: string; entityId?: string }; options?: Options }) {
    const url = new UrlAssembler()
      .segment('/services/search/:resource')
      .param('resource', 'customer')
      .query({ ...hookOptions.query })
      .toString();
    return useAxios<{ results: Customer[] }>(
      {
        url: decodeURIComponent(url),
        method: 'GET',
      },
      hookOptions?.options
    );
  },

  useFetchAllCustomers() {
    const url = new UrlAssembler().segment('/services/customers').toString();
    return useAxios({
      url: url,
      method: 'GET',
    });
  },

  useFetchAvailabilityGraphData(hookOptions: {
    variables?: { filters: Pick<FetchAvailabilityInput['filters'], 'startTime' | 'endTime' | 'tz' | 'employeeIds'> };
    options?: Options;
  }) {
    const url = new UrlAssembler().segment('/services/availability/graph').toString();
    return useAxios<TimeKeepingGraphData[]>(
      {
        url,
        method: 'POST',
        data: hookOptions.variables,
      },
      hookOptions.options
    );
  },

  useFetchSelfPreferenceByCategory(hookOptions: {
    variables: { category: PreferenceCategoryType };
    options?: Options;
  }) {
    const url = new UrlAssembler()
      .segment('/services/employees/me/preference')
      .query({ ...hookOptions.variables })
      .toString();

    return useAxios<{ preferences: EmployeePreference[] }>(
      {
        url,
        method: 'GET',
      },
      hookOptions.options
    );
  },

  updateSelfPreferenceByID(variables: { preferenceTypeId: string; payload: Record<string, any> }) {
    const url = new UrlAssembler().segment('/services/employees/me/preference').toString();
    return apiClient.post(url, variables);
  },

  useFetchBestShifts() {
    const url = new UrlAssembler().segment('/services/employees/next-best-shifts').toString();
    return useAxios({
      url: url,
      method: 'GET',
    });
  },

  createSelfPreferenceByName(variables: { preferenceTypeName: string; payload: Record<string, any> }) {
    const url = new UrlAssembler().segment('/services/employees/me/preference').toString();
    return apiClient.put(url, variables);
  },

  useFetchCapacityRequests(filters: SchedulingFilterInput, options?: Options) {
    const url = new UrlAssembler().segment('/services/capacity').toString();
    return useAxios<{
      schedule: CapacityRequest[];
      totalCount: number;
      currentPage: number;
      totalPages: number;
      totalRequestCount: number;
    }>(
      {
        url: url,
        method: 'POST',
        data: { filters },
      },
      options
    );
  },

  useFetchAllServices(options?: Options) {
    const url = new UrlAssembler().segment('/services/services').toString();
    return useAxios<{ rows: Service[]; pages: number; total: number }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  updateCapacityRequest(id: string, payload: CapacityRequestCapacityPayload) {
    const url = new UrlAssembler().segment('/services/capacity/:id').param('id', id).toString();
    return apiClient.put(url, payload);
  },

  useFetchCapacityRequestById(capacityId: string, options?: Options) {
    const url = new UrlAssembler()
      .segment('services/capacity/:capacity_id')
      .param('capacity_id', capacityId)
      .toString();
    return useAxios<CapacityRequestByIdResponse>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  useFetchCalendarActivityLogs(calendarId: string, options?: Options) {
    const url = new UrlAssembler()
      .segment('/services/calendar/:calendarId/activity-logs')
      .param({ calendarId })
      .toString();
    return useAxios<{ activityLogs: Array<ActivityLog> }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  useFetchEligibleEmployeesWithNotifications(calendarId: string, options?: Options) {
    const url = new UrlAssembler()
      .segment('/services/calendar/:calendarId/eligible-employees')
      .param({ calendarId })
      .toString();

    return useAxios<{
      eligibleEmployees: Array<{
        employeeId: string;
        eligibleOn?: string;
        totalScore?: number;
        fullName: string;
        jobRolesTitles?: string[];
        baseRate?: number;
        maxRate?: number;
        averageRate?: number;
      }>;
    }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  useFetchCalendarRequestsHistory(calendarId: string, variables: { timezone?: string }, options?: Options) {
    const url = new UrlAssembler()
      .segment('/services/calendar/:calendarId/requests-history')
      .param({ calendarId })
      .query({ ...variables })
      .toString();
    return useAxios<{
      requestsHistory: Array<
        ShiftRequest & {
          totalScore: number;
          totalHours: number;
          jobRolesTitles: string[];
          requestStartTime: string | null;
          requestEndTime: string | null;
        }
      >;
    }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  dispatchNotification(channel: 'byText' | 'byCall', resource: string, payload: { id: string; initiator: string }) {
    if (channel === 'byText') {
      const url = new UrlAssembler().segment('/services/workflows/notify/:channel').param({ channel }).toString();
      return apiClient.post(url, { resource: resource, payload });
    } else if (channel === 'byCall') {
      const newPayload = { shiftIds: [payload.id] };
      return apiClient.post('/services/scheduled_communications/schedule_call', newPayload);
    }
  },

  useFetchAllRoles(options?: Options) {
    const url = new UrlAssembler().segment('/services/roles').toString();
    return useAxios<{ roles: RolesResponse[] }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  useFetchEmployeeRoles(variables: { employeeId: string }, options?: Options) {
    const url = new UrlAssembler()
      .segment('/services/employees/:employeeId/roles')
      .param('employeeId', variables.employeeId)
      .toString();
    return useAxios<EmployeeRoleResponse>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  addRoleToEmployee(employeeId: string, roleId: string) {
    const url = new UrlAssembler().segment('/services/roles/employee_roles_by_id').toString();
    const data = {
      employeeRoles: [
        {
          employee_id: employeeId,
          role_id: roleId,
        },
      ],
    };

    return apiClient.post(url, data);
  },

  archiveEmployee(employeeId: string) {
    const url = new UrlAssembler().segment('/services/employees/:id').param('id', employeeId).toString();
    return apiClient.delete(url);
  },

  archiveEmployeeLicense(employeeId: string, licenseId: string, stateId: string) {
    const url = new UrlAssembler()
      .segment('/services/employees/:employeeId/license/:licenseId/:stateId/archive')
      .param('employeeId', employeeId)
      .param('licenseId', licenseId)
      .param('stateId', stateId)
      .toString();
    return apiClient.delete(url);
  },

  archiveEmployeesBulk(payload: EmployeesBulkArchivePayload) {
    const url = new UrlAssembler().segment('/services/employees/archive/bulk').toString();
    return apiClient.post(url, payload);
  },

  useFetchAllLicenses(options?: Options) {
    const url = new UrlAssembler().segment('/services/licenses').toString();
    return useAxios<{ data: License[] }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  addEmployeeLicense(payload: AddEmployeeLicensePayload) {
    const url = new UrlAssembler().segment('/services/employees/license/add').toString();
    return apiClient.post(url, payload);
  },

  useFetchTopPerformers(payload: { startTime: string; endTime: string }, options?: Options) {
    const url = new UrlAssembler().segment('/services/dashboard/top-performers').toString();
    return useAxios<{ topPerformers: Performer[] }>(
      {
        url: url,
        method: 'POST',
        data: payload,
      },
      options
    );
  },

  useFetchUnfilledShifts(payload: { startTime: string; endTime: string; entityIds: string[] }, options?: Options) {
    const url = new UrlAssembler().segment('/services/dashboard/unfilled-shifts').toString();
    return useAxios<{ unfilledShifts: UnfilledShift[] }>(
      {
        url: url,
        method: 'POST',
        data: payload,
      },
      options
    );
  },

  useFetchEmployeesEngaged(payload: { numOfWeeks: number; entityIds: string[] }, options?: Options) {
    const url = new UrlAssembler().segment('/services/dashboard/employees-engaged').toString();
    return useAxios<{ engagedEmployees: EngagedEmployee[] }>(
      {
        url: url,
        method: 'POST',
        data: payload,
      },
      options
    );
  },

  createTimeOffRequest(employeeId: string, payload: TimeOffRequest) {
    const url = new UrlAssembler()
      .segment('/services/employees/:employeeId/timeoff/request')
      .param('employeeId', employeeId)
      .toString();
    return apiClient.put(url, payload);
  },

  useFetchEmployeeTimeOffHistory(
    employeeId: string,
    variables: {
      startTime?: string | null;
      endTime?: string | null;
      allTime?: boolean;
      type: string;
    },
    options?: Options
  ) {
    const url = new UrlAssembler()
      .segment('/services/employees/:employeeId/timeoff/history')
      .param({ employeeId })
      .toString();
    return useAxios<{ history: Array<TimeOffHistoryResponse> }>(
      {
        url: url,
        method: 'POST',
        data: variables,
      },
      options
    );
  },

  useFetchEmployeeAvailableTimeOff(employeeId: string, options?: Options) {
    const url = new UrlAssembler()
      .segment('/services/employees/:employeeId/timeoff/available')
      .param({ employeeId })
      .toString();
    return useAxios<{
      result: {
        availableHoursByType: Record<string, number>;
        requestedHoursByType: Record<string, number>;
        hoursPerMonth: number;
        remainingHours: number;
        totalAvailableHours: number;
        totalRequestedHours: number;
      };
    }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  bulkApplyToShifts(employeeId: string, payload: BulkApplyPayload) {
    const url = new UrlAssembler()
      .segment('/services/employees/:employeeId/schedule/apply/multiple')
      .param({ employeeId })
      .toString();

    return apiClient.put<BulkApplyResponse>(url, payload);
  },

  useFetchCapacityEntities(options?: Options) {
    const url = new UrlAssembler().segment('/services/capacity/entities').toString();
    return useAxios<
      Array<{
        entity_id: string;
        entity_name: string;
        totalConfirmedHours: string;
        totalAvailableHours: number;
        totalRequestedHours: string;
      }>
    >(
      {
        url: url,
        method: 'POST',
      },
      options
    );
  },

  useFetchCapacityDaysByEntityId(
    params: { entityId: string; startTime: string; endTime: string; timezone: string },
    options?: Options
  ) {
    const url = new UrlAssembler()
      .segment('/services/capacity/byDays')
      .param({ ...params })
      .toString();
    return useAxios<
      Array<{
        availableSlots: number;
        blockedHoursUserTZ: Array<{
          startTime: string;
          endTime: string;
        }>;
        capacityRequests: Array<{
          id: string;
          startTime: string;
          endTime: string;
          totalHours: number;
          availableSlots: number;
        }>;
        date: string;
        dayOfWeek: string;
        totalCapacityHours: number;
        workdayHours: number;
        maxCapacityPerDay: number;
      }>
    >(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  applyCapacityRequest(
    params: { employeeId: string },
    payload: {
      type: 'capacity';
      applyRequest: {
        toEmployeeId: string;
      };
      capacity: {
        startTime: string;
        endTime: string;
        entityId: string;
        capacityId?: string;
      };
    }
  ) {
    const url = new UrlAssembler()
      .segment('/services/employees/:employeeId/schedule/capacity/apply')
      .param({ ...params })
      .toString();

    return apiClient.put(url, payload);
  },

  useFetchEntitySettings(id: string, options?: Options) {
    const url = new UrlAssembler()
      .segment('/services/entities/:id/settings')
      .param({
        id,
      })
      .toString();

    return useAxios<{
      settings: EntitySettings;
    }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  useFetchDashboardTotals(filters: DashboardFilters, options?: Options) {
    const url = new UrlAssembler().segment(`/services/dashboard`).toString();

    return useAxios(
      {
        url: url,
        method: 'POST',
        data: filters,
      },
      options
    );
  },

  useFetchEmployeeBonusCapacities(params: FetchBonusCapacityInput, options?: Options) {
    const url = new UrlAssembler().segment('/services/capacity/bonus').toString();
    return useAxios<{
      bonusCapacities: Array<BonusCapacity>;
    }>(
      {
        url: url,
        method: 'POST',
        data: params,
      },
      options
    );
  },

  useFetchComplianceDocuments(filters: ComplianceFilterInput, options?: Options) {
    const url = new UrlAssembler().segment('/services/compliance/documents').toString();
    return useAxios(
      {
        url: url,
        method: 'POST',
        data: { filters },
      },
      options
    );
  },

  useFetchComplianceMetrics(filters: ComplianceFilterInput, options?: Options) {
    const url = new UrlAssembler().segment('/services/compliance/metrics').toString();
    return useAxios(
      {
        url: url,
        method: 'POST',
        data: { filters },
      },
      options
    );
  },

  approveRejectComplianceDocument(params: { documentId: string; action: DocumentApprovalAction }, reason?: string) {
    const url = new UrlAssembler()
      .segment('/services/approvals/document/:documentId/:action')
      .param({ ...params })
      .toString();

    return apiClient.put(url, { reason });
  },

  useFetchComplianceDocumentURL(params: { documentId: string }, options?: Options) {
    const url = new UrlAssembler()
      .segment('/services/compliance/documents/:documentId/signed_url')
      .param('documentId', params.documentId)
      .toString();

    return useAxios(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  useFetchComplianceDocumentTypes(options?: Options) {
    const url = new UrlAssembler().segment('/services/compliance/document_types').toString();
    return useAxios<{ id: string; name: string }[]>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  useFetchOpenShifts(filters: OpenShiftsFilterInput, options?: Options) {
    const url = new UrlAssembler().segment('/services/patients/open-shifts').toString();
    return useAxios<{ results: PatientApiResult[] }>(
      {
        url: url,
        method: 'POST',
        data: { ...filters },
      },
      options
    );
  },

  useFetchPatientOpenShifts(payload: OpenShiftPayload, patientId: string, options?: Options) {
    const url = new UrlAssembler().segment('/services/patients/:patientId/open-shifts').param({ patientId }).toString();
    return useAxios<OpenShiftByPatientEntity[]>(
      {
        url: url,
        method: 'POST',
        data: payload,
      },
      options
    );
  },

  useFetchPatientEntityDetails(id: string, options?: Options) {
    const url = new UrlAssembler().segment('/services/patients/:id/details').param({ id }).toString();

    return useAxios<PatientDetails>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  useBulkUserUpload(options?: Options) {
    const url = new UrlAssembler().segment('/services/admin/users/bulk_upload').toString();

    return useAxios<BulkUserCreationResponse>(
      {
        url: url,
        method: 'POST',
        headers: {
          'Content-Type': 'multipart/form-data; boundary=<calculated when request is sent>',
        },
      },
      options
    );
  },

  useFetchEntityMaxClinicianSettings(entityId: string, payload: EntityMaxClinicianSettingsPayload, options?: Options) {
    const url = new UrlAssembler()
      .segment('/services/entities/:entityId/max-clinicians')
      .param({ entityId })
      .toString();

    return useAxios<EntityMaxCliniciansSettingsResponse>(
      {
        url: url,
        method: 'POST',
        data: payload,
      },
      options
    );
  },

  useArchiveEntityEmployment(payload: EntityEmploymentArchivePayload, options?: Options) {
    const url = new UrlAssembler().segment('/services/employees/archive/entity_employment').toString();
    return useAxios<EntityEmploymentArchiveResponse[]>(
      {
        url: url,
        method: 'POST',
        data: payload,
      },
      options
    );
  },

  useBulkApproveShiftRequests(payload: { employeeId: string; patientId: string }, options?: Options) {
    const url = new UrlAssembler().segment('/services/patients/bulk-approve').toString();
    return useAxios<BulkApproveShiftsResponse>(
      {
        url: url,
        method: 'POST',
        data: payload,
      },
      options
    );
  },

  useBulkRejectShiftRequests(payload: { employeeId: string; patientId: string }, options?: Options) {
    const url = new UrlAssembler().segment('/services/patients/bulk-reject').toString();
    return useAxios<BulkRejectShiftsResponse>(
      {
        url: url,
        method: 'POST',
        data: payload,
      },
      options
    );
  },

  useFetchAllResourcePermissions(options?: Options) {
    const url = new UrlAssembler().segment('/services/permissions').toString();
    return useAxios<{ resourcePermissions: ResourcePermission[] }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  useFetchEmployeeResourcePermissions(employeeId: string, roleId: string, options?: Options) {
    const url = new UrlAssembler()
      .segment('/services/employees/:employeeId/role/:roleId/permissions')
      .param('employeeId', employeeId)
      .param('roleId', roleId)
      .toString();
    return useAxios<EmployeeRoleResourcePermissionResponse>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  useAddResourcePermissionToEmployee(
    payload: { userPolicy: UpdateEmployeeResourcePermissionsPayload },
    options?: Options
  ) {
    const url = new UrlAssembler().segment('/services/permissions/create_policy').toString();
    return useAxios(
      {
        url: url,
        method: 'POST',
        data: payload,
      },
      options
    );
  },

  useRemoveResourcePermissionFromEmployee(
    payload: { userPolicy: UpdateEmployeeResourcePermissionsPayload },
    options?: Options
  ) {
    const url = new UrlAssembler().segment('/services/permissions/delete_policy').toString();
    return useAxios(
      {
        url: url,
        method: 'DELETE',
        data: payload,
      },
      options
    );
  },

  createAttributePolicy(payload: CreateAttributePolicyPayload) {
    const url = new UrlAssembler().segment('/services/permissions/create_attribute_policy').toString();
    return apiClient.post<{ createdPolicy: CreateAttributePolicyResponse }>(url, payload);
  },

  deleteAttributePolicy(id: string) {
    const url = new UrlAssembler().segment('/services/permissions/attribute_policies/:id').param('id', id).toString();
    return apiClient.delete(url);
  },

  restoreAttributePolicy(id: string) {
    const url = new UrlAssembler()
      .segment('/services/permissions/attribute_policies/:id/restore')
      .param('id', id)
      .toString();
    return apiClient.post(url);
  },

  updateAttributePolicy(id: string, payload: UpdateAttributePolicyPayload) {
    const url = new UrlAssembler().segment('/services/permissions/attribute_policies/:id').param('id', id).toString();
    return apiClient.put(url, payload);
  },

  useFetchAttributePolicies(objectId: string, options?: Options) {
    const url = new UrlAssembler()
      .segment('/services/permissions/attribute_policies/:objectId')
      .param('objectId', objectId)
      .toString();
    return useAxios<{ attributePermissions: CreateAttributePolicyResponse[] }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  useFetchEntityEmploymentsByIds(payload: { employeeIds: string[] }, options?: Options) {
    const url = new UrlAssembler().segment('/services/employees/entity_employments/employee_ids').toString();
    return useAxios<string[]>(
      {
        url: url,
        method: 'POST',
        data: payload,
      },
      options
    );
  },

  useFetchManagedEmployeesByManagerId(managerId: string, options?: Options) {
    const url = new UrlAssembler()
      .segment('/services/employees/manager_employees/:managerId')
      .param('managerId', managerId)
      .toString();
    return useAxios<{ employees: ManagedEmployeeResponse[] }>(
      {
        url: url,
        method: 'GET',
      },
      options
    );
  },

  useFetchEmployeeRouteByDate(payload: { employeeId: string; date: string }, options?: Options) {
    const url = new UrlAssembler().segment('/services/employees/route-planner').toString();
    const namespace = process.env.ARYA_PLATFORM === 'production' ? 'cache_prod' : 'cache_staging';
    return useAxios<ICustomersRouteResponse>(
      {
        url: url,
        method: 'POST',
        data: {
          ...payload,
          namespace,
        },
      },
      options
    );
  },

  saveEmployeeRoute(payload: { employeeId: string; date: string; shiftSequence: string[]; shifts: any[] }) {
    const url = new UrlAssembler().segment('/services/employees/route-planner/save').toString();
    const namespace = process.env.ARYA_PLATFORM === 'production' ? 'cache_prod' : 'cache_staging';
    return apiClient.post(url, { ...payload, namespace });
  },

  /**
   * Admin Dashboard endpoints above this line
   */

  useFetchEmployeeProfilePhotoDocuments(filters: any, options?: Options) {
    const url = new UrlAssembler().segment('/services/employees/employee-profile-photos').toString();
    // need to update with return type
    return useAxios(
      {
        url: url,
        method: 'POST',
        data: { filters },
      },
      options
    );
  },

  approveProfilePhoto(documentId: string) {
    const url = new UrlAssembler()
      .segment('/services/employees/employee-profile-photos/:documentId/approve')
      .param('documentId', documentId)
      .toString();

    return apiClient.put(url);
  },

  rejectProfilePhoto(documentId: string, reason: string) {
    const url = new UrlAssembler()
      .segment('/services/employees/employee-profile-photos/:documentId/reject')
      .param('documentId', documentId)
      .toString();

    return apiClient.post(url, { documentId, reason });
  },

  fetchEmployeeProfilePhotoSignedURL(documentId: string) {
    const url = new UrlAssembler()
      .segment('/services/employees/employee-profile-photos/:documentId/signed_url')
      .param('documentId', documentId)
      .toString();

    return apiClient.get(url);
  },

  useFetchPatientsIntakeQueue(filters?: IntakeQueueFilters, options?: Options) {
    const url = new UrlAssembler().segment('/services/patients/intake-queue').toString();
    return useAxios<IntakeQueuePatient[]>(
      {
        url: url,
        method: 'POST',
        data: { ...filters },
      },
      options
    );
  },

  useFetchJobRoleCategoryNamesByIds(ids: string[], options?: Options) {
    const url = new UrlAssembler().segment('/services/entities/job_role_category_names/by_ids').toString();
    return useAxios(
      {
        url: url,
        method: 'POST',
        data: { ...ids },
      },
      options
    );
  },

  useFetchAssignedShifts(filters: FetchCalendarShiftsInput, options?: Options) {
    const url = new UrlAssembler().segment('/services/dashboard/assigned-shifts').toString();
    return useAxios<{ startTime: string; firstName: string; lastName: string }[]>(
      {
        url: url,
        method: 'POST',
        data: filters,
      },
      options
    );
  },
};

export default ApiService;
